<template>
  <div class="main-wrap">
    <main-header />
    <div class="container-general mb-4 pt-15">
      <v-container>
        <headline @search="handleSearch" />

        <!-- Results Info -->
        <v-row class="mt-8 mb-6 mx-0 mx-sm-2">
          <v-col>
            <div class="results-toolbar d-flex align-center justify-space-between flex-wrap px-4 px-md-6 py-4 py-md-5">
              <div class="toolbar-title-wrap">
                <h2 class="text-h5 font-weight-medium mb-1">
                  {{ filteredPosts.length }} {{ $t('blog.results') || 'Articles' }}
                </h2>
                <p v-if="searchQuery" class="text-body-2 mt-1 mb-0 grey--text text--darken-1">
                  <span>{{ $t('blog.searchFor') || 'Searching for' }}: "{{ searchQuery }}"</span>
                  <v-btn text x-small color="primary" @click="clearFilters" class="ml-2">
                    <v-icon small left>mdi-close-circle</v-icon>
                    {{ $t('blog.clear') || 'Clear' }}
                  </v-btn>
                </p>
              </div>

              <!-- Sort Options -->
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn outlined color="primary" v-bind="attrs" v-on="on" class="mt-3 mt-sm-0 sort-btn">
                    <v-icon left small>mdi-sort</v-icon>
                    {{ $t('blog.sort') || 'Sort By' }}
                  </v-btn>
                </template>
                <v-list class="sort-list py-1">
                  <v-list-item :class="{ 'active-sort': sortBy === 'newest' }" @click="sortBy = 'newest'">
                    <v-list-item-title>{{ $t('blog.newest') || 'Newest First' }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item :class="{ 'active-sort': sortBy === 'oldest' }" @click="sortBy = 'oldest'">
                    <v-list-item-title>{{ $t('blog.oldest') || 'Oldest First' }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item :class="{ 'active-sort': sortBy === 'title' }" @click="sortBy = 'title'">
                    <v-list-item-title>{{ $t('blog.title') || 'Title' }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </v-col>
        </v-row>

        <!-- Loading State -->
        <v-row v-if="loading">
          <v-col v-for="n in 3" :key="n" cols="12">
            <v-skeleton-loader type="article, actions" class="mb-6"></v-skeleton-loader>
          </v-col>
        </v-row>

        <!-- Blog Posts -->
        <div v-else-if="filteredPosts.length > 0">
          <v-row class="mx-0 mx-sm-1">
            <v-col v-for="(item, index) in filteredPosts" :key="item.id" cols="12" sm="6" md="4" class="mb-6 blog-post-item"
              :style="{ animationDelay: `${index * 0.1}s` }">
              <div class="d-flex justify-center post-card-shell">
                <post-card :orientation="'portrait'" :title="item.name[locale]" :desc="item.description[locale]"
                  :date="formatDate(item.createdAt || item.date)" type="over" :img="getImage(item.img)"
                  :href="`/bloger/${item.id}`" />
              </div>
            </v-col>
          </v-row>
        </div>

        <!-- Empty State -->
        <v-row v-else>
          <v-col cols="12">
            <v-card flat class="text-center py-12 empty-state-card">
              <v-icon size="100" color="grey lighten-2">mdi-file-document-outline</v-icon>
              <h3 class="text-h5 mt-4 grey--text">{{ $t('blog.noResults') || 'No articles found' }}</h3>
              <p class="text-body-1 mt-2 grey--text">
                {{ $t('blog.tryDifferent') || 'Try adjusting your search or filter' }}
              </p>
              <v-btn color="primary" outlined class="mt-4" @click="clearFilters">
                {{ $t('blog.clearFilters') || 'Clear Filters' }}
              </v-btn>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </div>
    <div id="footer">
      <main-footer class="mt-3" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "~/assets/pages";

.results-toolbar {
  border-radius: 18px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.86));
  border: 1px solid rgba(63, 81, 181, 0.16);
  box-shadow: 0 10px 30px rgba(37, 52, 125, 0.1);
  backdrop-filter: blur(6px);
}

.sort-btn {
  border-radius: 12px;
  font-weight: 600;
}

.sort-list {
  min-width: 220px;
}

.sort-list .active-sort {
  background: rgba(63, 81, 181, 0.08);
}

.blog-post-item {
  animation: fadeInUp 0.6s ease-out backwards;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
  }
}

.post-card-shell {
  width: 100%;
  border-radius: 16px;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.post-card-shell:hover {
  transform: translateY(-6px);
  box-shadow: 0 18px 34px rgba(63, 81, 181, 0.16);
}

.empty-state-card {
  border-radius: 16px;
  border: 1px solid rgba(63, 81, 181, 0.14);
  background: linear-gradient(180deg, #ffffff 0%, #fbfbff 100%) !important;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

::v-deep .v-skeleton-loader {
  background-color: transparent !important;
}

@media (max-width: 960px) {
  .results-toolbar {
    padding: 1rem !important;
    border-radius: 14px;
  }

  .toolbar-title-wrap {
    width: 100%;
  }

  .sort-btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 600px) {
  .results-toolbar {
    margin-top: 0.5rem;
  }

  .blog-post-item {
    margin-bottom: 0.8rem !important;
  }

  .empty-state-card {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }
}
</style>

<script>
import BlogHeader from "~/components/Header/BlogHeader";
import Headline from "~/components/Blog/Headline";
import PostCard from "~/components/Cards/PostCard";
import Footer from "~/components/Footer";

export default {
  data: () => ({
    posts: [],
    loading: true,
    searchQuery: '',
    sortBy: 'newest'
  }),
  async created() {
    try {
      this.loading = true;
      this.posts = (await this.$axios.get("/bloger")).data;
    } catch (error) {
      console.error('Error loading blog posts:', error);
    } finally {
      this.loading = false;
    }
  },
  components: {
    "main-header": BlogHeader,
    "main-footer": Footer,
    Headline,
    PostCard
  },
  head() {
    const title = this.$t("seo.blog.title");
    const desc = this.$t("seo.blog.desc");
    return {
      title,
      meta: [
        { hid: "description", name: "description", content: desc },
        { hid: "og:title", property: "og:title", content: title },
        { hid: "og:description", property: "og:description", content: desc },
        { hid: "og:url", property: "og:url", content: `https://www.motanamy.com${this.$route.path}` },
        { hid: "twitter:title", name: "twitter:title", content: title },
        { hid: "twitter:description", name: "twitter:description", content: desc },
      ],
    };
  },
  methods: {
    getImage(icon) {
      return `${this.geteRelativeUrl()}file/assets?path=${icon}`;
    },
    handleSearch(query) {
      this.searchQuery = query ? query.toLowerCase().trim() : '';
    },
    clearFilters() {
      this.searchQuery = '';
    },
    formatDate(date) {
      if (!date) return '12 Nov 2020'; // Fallback

      try {
        const d = new Date(date);
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return d.toLocaleDateString(this.locale, options);
      } catch (error) {
        return '12 Nov 2020';
      }
    }
  },
  computed: {
    locale() {
      return this.$i18n.locale;
    },
    isMobile() {
      const smDown = this.$store.state.breakpoints.smDown;
      return smDown.indexOf(this.$mq) > -1;
    },
    filteredPosts() {
      let filtered = [...this.posts];

      // Apply search filter
      if (this.searchQuery) {
        filtered = filtered.filter(post => {
          const title = (post.name[this.locale] || '').toLowerCase();
          const desc = (post.description[this.locale] || '').toLowerCase();

          return title.includes(this.searchQuery) ||
            desc.includes(this.searchQuery);
        });
      }

      // Apply sorting
      if (this.sortBy === 'newest') {
        filtered.sort((a, b) => {
          const dateA = new Date(a.createdAt || a.date || 0);
          const dateB = new Date(b.createdAt || b.date || 0);
          return dateB - dateA;
        });
      } else if (this.sortBy === 'oldest') {
        filtered.sort((a, b) => {
          const dateA = new Date(a.createdAt || a.date || 0);
          const dateB = new Date(b.createdAt || b.date || 0);
          return dateA - dateB;
        });
      } else if (this.sortBy === 'title') {
        filtered.sort((a, b) => {
          const titleA = (a.name[this.locale] || '').toLowerCase();
          const titleB = (b.name[this.locale] || '').toLowerCase();
          return titleA.localeCompare(titleB);
        });
      }

      return filtered;
    }
  }
};
</script>
